<template>
  <b-modal
    id="modal-participant-approval"
    ref="modalParticipantApproval"
    title="Approval Peserta"
    size="xl"
    hide-footer
    @hidden="resetModal">
    <div v-if="selectedBatch">
      <!-- Batch Info Header -->
      <div class="mb-4">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <h5 class="mb-2">{{ selectedCourse?.title }} - {{ formatBatchLabel(selectedBatch) }}</h5>
            <div class="d-flex gap-2 align-items-center">
              <span class="badge" :class="getBatchStatusClass(selectedBatch.batch_status)">
                {{ selectedBatch.batch_status }}
              </span>
              <span class="text-muted">•</span>
              <small class="text-muted">
                Quota: {{ selectedBatch.quota }} | Enrolled: {{ selectedBatch.enrolled }}
              </small>
            </div>
          </div>
          <div>
            <span class="badge bg-primary">
              {{ selectedParticipants.length }} Selected
            </span>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-3 text-muted mb-0">Memuat peserta...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="participants.length === 0" class="text-center py-5">
        <i class="fe fe-users" style="font-size: 3rem; color: #dee2e6;"></i>
        <p class="mt-3 text-muted mb-0">Tidak ada peserta.</p>
      </div>

      <!-- Participants Table -->
      <div v-else class="table-responsive">
        <table class="table table-sm table-hover">
          <thead>
            <tr>
              <th style="width: 40px;">
                <div class="custom-control custom-checkbox">
                  <input 
                    type="checkbox" 
                    class="custom-control-input" 
                    id="selectAll"
                    :checked="isAllSelected"
                    @change="toggleSelectAll">
                  <label class="custom-control-label" for="selectAll"></label>
                </div>
              </th>
              <th style="width: 40px;">No</th>
              <th>NIK</th>
              <th>Full Name</th>
              <th>Type</th>
              <th>Position</th>
              <th>Company</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(participant, index) in paginatedParticipants" :key="participant.tna_id">
              <td>
                <div class="custom-control custom-checkbox">
                  <input 
                    type="checkbox" 
                    class="custom-control-input" 
                    :id="`participant-${participant.tna_id}`"
                    :value="participant.tna_id"
                    v-model="selectedParticipants"
                    :disabled="!isParticipantActionable(participant)">
                  <label class="custom-control-label" :for="`participant-${participant.tna_id}`"></label>
                </div>
              </td>
              <td>{{ participantStartNumber + index }}</td>
              <td>
                <span class="badge badge-soft-primary">{{ participant.nik }}</span>
              </td>
              <td>{{ participant.fullname }}</td>
              <td>
                <span
                  class="badge"
                  :class="participant.is_mandatory ? 'badge-soft-danger' : 'badge-soft-info'">
                  {{ participant.is_mandatory ? 'Mandatory' : 'Development' }}
                </span>
              </td>
              <td>{{ participant.position_level }}</td>
              <td>
                <small class="text-muted">{{ participant.company }}</small>
              </td>
              <td>
                <span
                  class="badge"
                  :class="getParticipantStatusClass(participant.status)">
                  {{ getParticipantStatusLabel(participant.status) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="participants.length > pageSize"
        class="d-flex justify-content-between align-items-center mt-3">
        <small class="text-muted">
          Showing {{ participantStartNumber }} - {{ participantEndNumber }} of {{ participants.length }} participants
        </small>
        <div class="d-flex align-items-center gap-2">
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            :disabled="participantPage === 1"
            @click="participantPage -= 1">
            Previous
          </button>
          <span class="badge badge-soft-secondary">
            {{ participantPage }} / {{ participantTotalPages }}
          </span>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            :disabled="participantPage === participantTotalPages"
            @click="participantPage += 1">
            Next
          </button>
        </div>
      </div>

      <!-- Action Buttons -->
      <div v-if="hasPendingParticipants" class="mt-4 d-flex gap-2 justify-content-end">
        <button
          type="button"
          class="btn btn-secondary"
          @click="closeModal">
          Close
        </button>
        <button
          type="button"
          class="btn btn-success"
          :disabled="isApproving || selectedParticipants.length === 0"
          @click="approveParticipants()">
          <span v-if="isApproving">
            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Menyetujui...
          </span>
          <span v-else>
            <i class="fe fe-check me-2"></i>
            Approve
          </span>
        </button>
        <button
          type="button"
          class="btn btn-outline-success"
          :disabled="isApproving || actionableParticipants.length === 0"
          @click="approveAllParticipants()">
          <i class="fe fe-check-circle me-2"></i>
          Approve All ({{ actionableParticipants.length }})
        </button>
        <button
          type="button"
          class="btn btn-danger"
          :disabled="isRejecting || selectedParticipants.length === 0"
          @click="rejectParticipants()">
          <span v-if="isRejecting">
            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Menolak...
          </span>
          <span v-else>
            <i class="fe fe-x me-2"></i>
            Reject
          </span>
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { ROLE_ADMIN_PJO, ROLE_HSE } from '@/constants/mitraRole';
import { normalizeErrorMessage } from '@/utils/error-message';
import { formatBatchLabel } from '@/utils/batchIdentity';

export default {
  name: 'ModalParticipantApproval',
  props: {
    selectedCourse: {
      type: Object,
      default: null
    },
    selectedBatch: {
      type: Object,
      default: null
    },
    batchIndex: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      selectedParticipants: [],
      loading: false,
      isApproving: false,
      isRejecting: false,
      participantPage: 1,
      pageSize: 10
    };
  },
  watch: {
    selectedBatch: {
      immediate: true,
      handler(newBatch) {
        if (newBatch && newBatch.company_participants) {
          // Auto-select all pending participants when batch is loaded
          this.selectedParticipants = newBatch.company_participants
            .filter(p => this.isParticipantActionable(p))
            .map(p => p.tna_id);
          this.participantPage = 1;
        }
      }
    }
  },
  computed: {
    ...mapGetters('users', ['profileData']),

    participants() {
      return this.selectedBatch?.company_participants || [];
    },

    paginatedParticipants() {
      const start = (this.participantPage - 1) * this.pageSize;
      return this.participants.slice(start, start + this.pageSize);
    },

    participantTotalPages() {
      return Math.max(1, Math.ceil(this.participants.length / this.pageSize));
    },

    participantStartNumber() {
      if (this.participants.length === 0) return 0;
      return ((this.participantPage - 1) * this.pageSize) + 1;
    },

    participantEndNumber() {
      return Math.min(this.participantPage * this.pageSize, this.participants.length);
    },

    userRoles() {
      const roles = this.profileData?.roles_uenroll || [];
      if (roles.length) return roles;
      return this.profileData?.role_id ? [this.profileData.role_id] : [];
    },

    activeApprovalStatus() {
      if (this.userRoles.includes(ROLE_ADMIN_PJO)) return 'Waiting for PJO Approval';
      if (this.userRoles.includes(ROLE_HSE)) return 'Waiting for Admin HSE Approval';
      return null;
    },
    
    hasPendingParticipants() {
      return this.participants.some(p => this.isParticipantActionable(p));
    },

    actionableParticipants() {
      return this.participants.filter(p => this.isParticipantActionable(p));
    },
    
    isAllSelected() {
      const pendingParticipants = this.actionableParticipants;
      return pendingParticipants.length > 0 && 
             this.selectedParticipants.length === pendingParticipants.length;
    }
  },
  methods: {
    ...mapActions('mitraEnrollmentReport', {
      approveParticipantsAction: 'approveParticipants',
      rejectParticipantsAction: 'rejectParticipants'
    }),
    formatBatchLabel,
    
    resetModal() {
      this.selectedParticipants = [];
      this.participantPage = 1;
    },
    
    closeModal() {
      this.$refs.modalParticipantApproval.hide();
    },
    
    toggleSelectAll(event) {
      if (event.target.checked) {
        // Select all pending participants
        this.selectedParticipants = this.actionableParticipants.map(p => p.tna_id);
      } else {
        // Deselect all
        this.selectedParticipants = [];
      }
    },
    
    async approveParticipants(participantIds = null) {
      const idsToApprove = Array.isArray(participantIds)
        ? [...participantIds]
        : [...this.selectedParticipants];
      if (idsToApprove.length === 0) return;

      const result = await this.$swal({
        title: 'Approve Peserta?',
        html: `Anda akan approve <strong>${idsToApprove.length}</strong> peserta.<br/>Action ini tidak dapat dibatalkan.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Ya, Approve',
        confirmButtonColor: '#00c9a7',
        cancelButtonText: 'Batal',
        reverseButtons: true
      });
      
      if (result.isConfirmed) {
        this.isApproving = true;
        try {
          await this.approveParticipantsAction({
            batchId: this.selectedBatch.training_event_id,
            data: { approved: idsToApprove }
          });

          this.$swal({
            title: 'Success',
            text: `${idsToApprove.length} peserta berhasil diapprove.`,
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });

          // Reset selections and close modal
          this.selectedParticipants = [];
          this.closeModal();
          
          // Refresh batch data
          this.$emit('refresh-batch');
        } catch (error) {
          // Extract error message from response
          const errorMessage = error.response?.data?.error || 
                               error.response?.data?.message || 
                               error.message || 
                               'Peserta belum berhasil disetujui';
          
          this.$swal({
            title: 'Error',
            text: normalizeErrorMessage(errorMessage),
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });
        } finally {
          this.isApproving = false;
        }
      }
    },

    approveAllParticipants() {
      return this.approveParticipants(this.actionableParticipants.map(p => p.tna_id));
    },
    
    async rejectParticipants() {
      const result = await this.$swal({
        title: 'Reject Peserta?',
        html: `Anda akan reject <strong>${this.selectedParticipants.length}</strong> peserta.<br/>Action ini tidak dapat dibatalkan.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ya, Reject',
        confirmButtonColor: '#e63757',
        cancelButtonText: 'Batal',
        reverseButtons: true
      });
      
      if (result.isConfirmed) {
        this.isRejecting = true;
        try {
          await this.rejectParticipantsAction({
            batchId: this.selectedBatch.training_event_id,
            data: { tna_ids: this.selectedParticipants }
          });

          this.$swal({
            title: 'Success',
            text: `${this.selectedParticipants.length} peserta berhasil direject.`,
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });

          // Reset selections and close modal
          this.selectedParticipants = [];
          this.closeModal();
          
          // Refresh batch data
          this.$emit('refresh-batch');
        } catch (error) {
          // Extract error message from response
          const errorMessage = error.response?.data?.error || 
                               error.response?.data?.message || 
                               error.message || 
                               'Peserta belum berhasil ditolak';
          
          this.$swal({
            title: 'Error',
            text: normalizeErrorMessage(errorMessage),
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#e63757'
          });
        } finally {
          this.isRejecting = false;
        }
      }
    },
    
    getBatchStatusClass(status) {
      const classes = {
        'upcoming': 'badge-soft-warning',
        'ongoing': 'badge-soft-success',
        'completed': 'badge-soft-info',
        'cancelled': 'badge-soft-danger'
      };
      return classes[status?.toLowerCase()] || 'badge-soft-secondary';
    },

    isParticipantActionable(participant) {
      return participant?.status === this.activeApprovalStatus;
    },

    getParticipantStatusLabel(status) {
      const labels = {
        'Waiting for PJO Approval': 'Approval PJO',
        'Waiting for Admin HSE Approval': 'Approval HSE Training',
        'Approved': 'Enrolled',
        'Rejected': 'Rejected',
        'Cancelled': 'Cancelled',
        'Completed': 'Completed',
        'Not Completed': 'Not Completed'
      };
      return labels[status] || status || 'Pending';
    },

    getParticipantStatusClass(status) {
      const classes = {
        'Waiting for PJO Approval': 'badge-soft-warning',
        'Waiting for Admin HSE Approval': 'badge-soft-warning',
        'Approved': 'badge-soft-success',
        'Rejected': 'badge-soft-danger',
        'Cancelled': 'badge-soft-danger',
        'Completed': 'badge-soft-success',
        'Not Completed': 'badge-soft-danger'
      };
      return classes[status] || 'badge-soft-secondary';
    }
  }
};
</script>

<style scoped>
.me-2 {
  margin-right: 0.5rem;
}

.gap-2 {
  gap: 0.5rem;
}

.badge-soft-primary {
  color: #2c7be5;
  background-color: rgba(44, 123, 229, 0.1);
}

.badge-soft-warning {
  color: #f6c343;
  background-color: rgba(246, 195, 67, 0.1);
}

.badge-soft-success {
  color: #00c9a7;
  background-color: rgba(0, 201, 167, 0.1);
}

.badge-soft-info {
  color: #39afd1;
  background-color: rgba(57, 175, 209, 0.1);
}

.badge-soft-danger {
  color: #e63757;
  background-color: rgba(230, 55, 87, 0.1);
}

.badge-soft-secondary {
  color: #6c757d;
  background-color: rgba(108, 117, 125, 0.1);
}

.bg-primary {
  background-color: #2c7be5 !important;
  color: white;
}
</style>
